import type { LensCard, LensCardView, LensDataset, LensFilter } from '~/types/leLens';

import { type Consolidation, type PlannedDataset, extractConsolidation } from './buildPlan';
import type { Ask } from './buildSession';
import type { Check } from './checkSql';
import { writeDatasets } from './combineQueries';
import type { BuildPool } from './lensAskSession';
import { type GateRow, sharedRow } from './planGateResolve';
import { type VerifiedSource, consolidatePrompt } from './prompts';

/** A group worth one shared query: the dataset proposed for it, and the rows
 *  that agreed to read from it. */
export type PlannedGroup = { key: string; rows: Array<GateRow> };

export type PlanConsolidation = { model: Consolidation; groups: Array<PlannedGroup> };

/**
 * A group of one is left alone: it is a single query either way, so sharing
 * buys the reader nothing, and the per-card build path writes a better card
 * (its own title, its own value format) than a dataset plus a view does.
 */
const WORTH_SHARING = 2;

/**
 * Groups the queries a plan would run, before the reader is shown the plan.
 *
 * A sixteen-card plan asks for sixteen queries unless something notices that
 * most of them are the same scan cut differently. That noticing used to happen
 * after approval, so the number the reader approved was never the number that
 * ran. Doing it here means the plan states what it will actually do.
 *
 * Rows that could not be grouped come back unchanged and keep their own query.
 */
export async function consolidatePlannedRows(args: {
  ask: Ask;
  onStatus: (message: string | null) => void;
  rows: ReadonlyArray<GateRow>;
  /** Found while the plan was still being written, so this turn only groups. */
  sources?: ReadonlyArray<VerifiedSource>;
}): Promise<{ rows: Array<GateRow>; consolidation: PlanConsolidation | null }> {
  const first = await groupPlannedRows(args);
  if (!first.consolidation) {
    return first;
  }
  const alone = first.rows.filter((row) => row.status === 'new' && !row.plannedQueryKey);
  if (alone.length < WORTH_SHARING) {
    return first;
  }
  const again = await groupPlannedRows({ ...args, keySuffix: '#2', rows: first.rows });
  if (!again.consolidation) {
    return first;
  }
  const numbering = new Map(args.rows.filter((row) => row.status === 'new').map((row, index) => [row.id, index + 1]));
  return {
    rows: again.rows,
    consolidation: {
      groups: [...first.consolidation.groups, ...again.consolidation.groups],
      model: {
        assignments: again.rows.flatMap((row) => {
          const index = numbering.get(row.id);
          return row.plannedQueryKey && index
            ? [{ datasetKey: row.plannedQueryKey, index, ...(row.plannedView ? { view: row.plannedView } : {}) }]
            : [];
        }),
        datasets: [...first.consolidation.model.datasets, ...again.consolidation.model.datasets],
        ...(first.consolidation.model.sources?.length ? { sources: first.consolidation.model.sources } : {}),
      },
    },
  };
}

async function groupPlannedRows({
  ask,
  keySuffix,
  onStatus,
  rows,
  sources,
}: {
  ask: Ask;
  keySuffix?: string;
  onStatus: (message: string | null) => void;
  rows: ReadonlyArray<GateRow>;
  sources?: ReadonlyArray<VerifiedSource>;
}): Promise<{ rows: Array<GateRow>; consolidation: PlanConsolidation | null }> {
  const owning = rows.filter((row) => row.status === 'new' && !row.plannedQueryKey);
  if (owning.length < WORTH_SHARING) {
    return { consolidation: null, rows: [...rows] };
  }

  const cards = owning.map((row) => ({ request: row.request, title: row.title, type: row.type }));

  onStatus(keySuffix ? 'Looking again at the cards left on their own…' : 'Looking for cards that can share a query…');
  const asked = await ask(consolidatePrompt(cards, { sources }));
  let model = asked.streamError ? null : suffixed(extractConsolidation(asked.answer, owning.length), keySuffix);

  if (!model?.datasets.length) {
    return { consolidation: null, rows: [...rows] };
  }

  // One dataset whose every column another one also carries is that other one
  // summed. Folding it in is not this code's call to make: two datasets can
  // share every column and still cover different periods, which only the model
  // that wrote the requests knows. So the fact is put to it once, and whatever
  // comes back stands.
  const rollup = strictRollup(model);
  if (rollup) {
    onStatus('Checking whether two of those queries are one query…');
    const again = await ask(consolidatePrompt(cards, { correction: rollupCorrection(rollup), sources }));
    const revised = again.streamError ? null : suffixed(extractConsolidation(again.answer, owning.length), keySuffix);
    if (revised?.datasets.length) {
      model = revised;
    }
  }

  const viewByRowId = new Map<string, { key: string; view?: LensCardView }>();
  const rowsByKey = new Map<string, Array<GateRow>>();
  for (const assignment of model.assignments) {
    // The model numbers the cards it was given from 1.
    const row = owning[assignment.index - 1];
    if (!row || !model.datasets.some((dataset) => dataset.key === assignment.datasetKey)) {
      continue;
    }
    viewByRowId.set(row.id, { key: assignment.datasetKey, ...(assignment.view ? { view: assignment.view } : {}) });
    rowsByKey.set(assignment.datasetKey, [...(rowsByKey.get(assignment.datasetKey) ?? []), row]);
  }

  const groups = [...rowsByKey.entries()]
    .filter(([, grouped]) => grouped.length >= WORTH_SHARING)
    .map(([key, grouped]) => ({ key, rows: grouped }));
  const sharedKeys = new Set(groups.map((group) => group.key));

  if (!groups.length) {
    return { consolidation: null, rows: [...rows] };
  }

  return {
    consolidation: {
      groups,
      model: {
        assignments: model.assignments,
        datasets: model.datasets.filter((dataset) => sharedKeys.has(dataset.key)),
        ...(sources?.length ? { sources } : model.sources?.length ? { sources: model.sources } : {}),
      },
    },
    rows: rows.map((row) => {
      const assigned = viewByRowId.get(row.id);
      if (!assigned || !sharedKeys.has(assigned.key)) {
        return row;
      }
      const dataset = model.datasets.find((entry) => entry.key === assigned.key);
      const readers = rowsByKey.get(assigned.key)?.length ?? 0;
      return sharedRow(
        { ...row, plannedQueryKey: assigned.key, ...(assigned.view ? { plannedView: assigned.view } : {}) },
        dataset?.name ?? assigned.key,
        readers,
      );
    }),
  };
}

/** Keys the model chose afresh each pass, kept apart so a second pass cannot
 *  claim a first pass dataset's key. */
function suffixed(model: Consolidation | null, keySuffix?: string): Consolidation | null {
  if (!model || !keySuffix) {
    return model;
  }
  return {
    ...model,
    assignments: model.assignments.map((assignment) => ({
      ...assignment,
      datasetKey: `${assignment.datasetKey}${keySuffix}`,
    })),
    datasets: model.datasets.map((dataset) => ({ ...dataset, key: `${dataset.key}${keySuffix}` })),
  };
}

/**
 * A dataset every column of which another proposed dataset also returns.
 *
 * That makes the first one the second one summed: the same numbers, read twice
 * from the warehouse. Ties are resolved toward the dataset with more columns,
 * since that is the one the other could be derived from.
 */
export function strictRollup(model: Consolidation): { coarse: PlannedDataset; finer: PlannedDataset } | null {
  for (const coarse of model.datasets) {
    const finer = model.datasets.find(
      (other) =>
        other.key !== coarse.key &&
        other.columns.length > coarse.columns.length &&
        coarse.columns.every((column) => other.columns.includes(column)),
    );
    if (finer) {
      return { coarse, finer };
    }
  }
  return null;
}

/** States the overlap as a fact and asks for one answer, not a preference. */
export function rollupCorrection({ coarse, finer }: { coarse: PlannedDataset; finer: PlannedDataset }): string {
  return (
    `“${finer.name}” returns every column “${coarse.name}” returns, so summing it over ` +
    `${finer.columns.filter((column) => !coarse.columns.includes(column)).join(', ')} produces “${coarse.name}”. ` +
    `Reply again with “${coarse.name}” folded into “${finer.name}”, its cards assigned to “${finer.name}”, and any ` +
    `rate among them expressed as a "ratio" of the two columns it divides. If the two genuinely cannot be one query, ` +
    `because they cover different periods or different filters, keep them apart and say so in the coarser dataset's ` +
    `"request".`
  );
}

/**
 * Writes the shared queries the plan promised and points the cards that agreed
 * to them at the result.
 *
 * A dataset that could not be written drops out, and its rows come back in
 * `remaining` for the per-card build path, so a failure here costs a shared
 * query rather than the cards.
 */
export async function buildPlannedDatasets({
  pool,
  consolidation,
  existingIds,
  filters,
  onStatus,
  rows,
  check,
}: {
  pool: BuildPool;
  consolidation: PlanConsolidation;
  existingIds: ReadonlyArray<string>;
  filters: ReadonlyArray<LensFilter>;
  onStatus: (message: string) => void;
  rows: ReadonlyArray<GateRow>;
  /** Supplied when the query is checked against the warehouse rather than by the model itself. */
  check?: Check;
}): Promise<{
  cards: Array<LensCard>;
  rowIdByCardId: ReadonlyMap<string, string>;
  datasets: Array<LensDataset>;
  remaining: Array<GateRow>;
}> {
  // Only the queries a surviving card reads: a group whose readers were all
  // removed at the gate would otherwise cost a turn and be saved unread.
  const wanted = new Set(rows.map((row) => row.plannedQueryKey).filter(Boolean));
  const planned = consolidation.model.datasets.filter((dataset) => wanted.has(dataset.key));
  if (!planned.length) {
    return { cards: [], rowIdByCardId: new Map(), datasets: [], remaining: [...rows] };
  }

  const { datasets, idByKey } = await writeDatasets({
    pool,
    existingIds: [...existingIds],
    filters: [...filters],
    model: { ...consolidation.model, datasets: planned },
    onStatus,
    ...(check ? { check } : {}),
  });

  const cards: Array<LensCard> = [];
  const rowIdByCardId = new Map<string, string>();
  const remaining: Array<GateRow> = [];
  const taken = new Set([...existingIds, ...datasets.map((dataset) => dataset.id)]);

  for (const row of rows) {
    const datasetId = row.plannedQueryKey ? idByKey.get(row.plannedQueryKey) : undefined;
    if (!datasetId || !row.plannedView) {
      remaining.push(row);
      continue;
    }

    const id = uniqueCardId(row.title || row.request || row.id, taken);
    taken.add(id);
    const card: LensCard = {
      id,
      type: row.type,
      title: row.title,
      ...(row.request ? { description: row.request } : {}),
      ...(row.plannedView ? { view: row.plannedView } : {}),
      datasetId,
    };
    cards.push(card);
    rowIdByCardId.set(card.id, row.id);
  }

  return { cards, rowIdByCardId, datasets, remaining };
}

/** Card ids share one space with the datasets written beside them, so a name
 *  taken by either is a name this cannot use. */
function uniqueCardId(source: string, taken: ReadonlySet<string>): string {
  const base =
    source
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'card';
  if (!taken.has(base)) {
    return base;
  }
  let suffix = 2;
  while (taken.has(`${base}-${suffix}`)) {
    suffix += 1;
  }
  return `${base}-${suffix}`;
}
